/**
 * Testimonials moderation (office staff). List + approve/unapprove with a hard minor
 * guard (a minor's testimonial can never be published), plus a quick add form.
 */
import { useCallback, useEffect, useState } from 'react';
import { Plus, CheckCircle2, EyeOff, ShieldAlert } from 'lucide-react';
import { api } from '../lib/apiClient.js';
import { useToast } from '../components/feedback/ToastProvider.jsx';
import { Modal } from '../components/ui/Modal.jsx';
import { Field, TextInput, Select } from '../components/ui/Field.jsx';
import { CATEGORY_META } from '../features/cases/caseMeta.js';
import styles from './Testimonials.module.css';

export default function Testimonials() {
  const toast = useToast();
  const [filter, setFilter] = useState('');
  const [state, setState] = useState({ status: 'loading', items: [] });
  const [showAdd, setShowAdd] = useState(false);

  const load = useCallback(async () => {
    setState((s) => ({ ...s, status: 'loading' }));
    try {
      const params = new URLSearchParams({ limit: '50' });
      if (filter) params.set('approved', filter);
      setState({ status: 'ready', items: await api.get(`/testimonials?${params}`) });
    } catch (err) {
      setState({ status: 'error', items: [], error: err.message });
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (t) => {
    try {
      await api.patch(`/testimonials/${t.id}/approve`, { approved: !t.approved });
      toast.success(t.approved ? 'Unpublished.' : 'Published to the public site.');
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <div>
          <p className="eyebrow">Supporters</p>
          <h1 className={styles.h1}>Testimonials</h1>
        </div>
        <button className={styles.primary} onClick={() => setShowAdd(true)}><Plus size={16} /> Add</button>
      </header>

      <div className={styles.filters}>
        {[['', 'All'], ['true', 'Published'], ['false', 'Pending']].map(([v, label]) => (
          <button key={v} className={`${styles.chip} ${filter === v ? styles.chipOn : ''}`} onClick={() => setFilter(v)}>{label}</button>
        ))}
      </div>

      <div className={styles.grid}>
        {state.status === 'loading' && Array.from({ length: 4 }).map((_, i) => <div key={i} className={styles.skel} />)}
        {state.status === 'ready' && state.items.length === 0 && <p className={styles.muted}>No testimonials in this filter.</p>}
        {state.status === 'ready' && state.items.map((t) => (
          <article key={t.id} className={`${styles.card} ${t.approved ? styles.published : ''}`}>
            <blockquote>{t.text}</blockquote>
            <div className={styles.meta}>
              <div>
                <strong>{t.authorLabel}</strong>
                <span>{t.village} · {CATEGORY_META[t.category]?.label || t.category}</span>
              </div>
              {t.isMinor && <span className={styles.minor}><ShieldAlert size={13} /> Minor — blocked</span>}
            </div>
            <button
              className={t.approved ? styles.unpublish : styles.publish}
              onClick={() => toggle(t)}
              disabled={t.isMinor && !t.approved}
            >
              {t.approved ? <><EyeOff size={15} /> Unpublish</> : <><CheckCircle2 size={15} /> Publish</>}
            </button>
          </article>
        ))}
      </div>

      <AddTestimonial open={showAdd} onClose={() => setShowAdd(false)} onAdded={() => { setShowAdd(false); load(); }} />
    </div>
  );
}

function AddTestimonial({ open, onClose, onAdded }) {
  const toast = useToast();
  const [form, setForm] = useState({ authorLabel: '', village: '', category: 'other', text: '', isMinor: false });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.text.trim().length < 10) return toast.error('Testimonial is too short.');
    setBusy(true);
    try {
      await api.post('/testimonials', { ...form, isMinor: form.isMinor });
      toast.success('Added — pending review.');
      setForm({ authorLabel: '', village: '', category: 'other', text: '', isMinor: false });
      onAdded();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Add testimonial" width={460}>
      <form onSubmit={submit} id="add-testimonial">
        <Field label="Author label (anonymised)" required><TextInput value={form.authorLabel} onChange={set('authorLabel')} placeholder="Gurpreet K." /></Field>
        <Field label="Village"><TextInput value={form.village} onChange={set('village')} /></Field>
        <Field label="Category"><Select value={form.category} onChange={set('category')}>{Object.entries(CATEGORY_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}</Select></Field>
        <Field label="Testimonial" required><textarea className={styles.textarea} rows={4} value={form.text} onChange={set('text')} /></Field>
        <label className={styles.check}>
          <input type="checkbox" checked={form.isMinor} onChange={(e) => setForm((f) => ({ ...f, isMinor: e.target.checked }))} />
          This person is a minor (will be blocked from publishing)
        </label>
      </form>
      <div className={styles.modalActions}>
        <button className={styles.ghost} onClick={onClose} type="button">Cancel</button>
        <button className={styles.primary} form="add-testimonial" type="submit" disabled={busy}>{busy ? 'Adding…' : 'Add'}</button>
      </div>
    </Modal>
  );
}
